import { Button, Popover, Tooltip, message } from 'antd';
import { HTMLAttributes, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import useDrawer from './createOrEdit/useDrawer';
import List, { ListProps } from '@knowledge/common/components/List';
import KnowledgeContentItem, {
  validateUploadValue,
} from './createOrEdit/formItem/KnowledgeContent';
import { EllipsisOutlined, FormOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { More } from 'views/Setting/AutoEmit/Refund/components/Task';
import { Formik } from 'formik';
import FormField from 'components/FormField';
import { useMemoizedFn } from 'ahooks';
import { updateKnowledge } from '@knowledge/service/api/spu';
import { Spu, SpuList } from '@knowledge/common/components/SpuBasic';
import titleBackgroundImage from '../../assets/titleBackground.png';
import useKnowledgeName from '@knowledge/common/hooks/useKnowledgeName';
import UploadEditor from '@knowledge/common/components/UploadEditor';
import {
  isEmptyDescendants,
  segments2descendants,
} from '@knowledge/common/components/UploadEditor/utils';
import { useGetSegmentsValue } from '@knowledge/common/share';

export interface KnowledgeProps extends HTMLAttributes<HTMLDivElement> {
  knowledge: Knowledge.SPU.Knowledge;
  onUpdate: (knowledge: Knowledge.SPU.Knowledge) => Promise<any>;
  onEdit: (knowledge: Knowledge.SPU.Knowledge) => void;
}

function Knowledge(props: KnowledgeProps) {
  const { knowledge, onEdit, onUpdate, ...otherProps } = props;

  const { knowledgeName: _knowledgeName, values, nameMultiLang } = knowledge;
  const knowledgeName = useKnowledgeName(nameMultiLang, _knowledgeName);

  const { t } = useTranslate();

  return (
    <Container {...otherProps}>
      <header>
        <div>
          <h4 title={knowledgeName}>{knowledgeName}</h4>
          <Tooltip title={t('marketing.编辑')}>
            <Button type="text" icon={<FormOutlined />} onClick={() => onEdit(knowledge)} />
          </Tooltip>
        </div>
      </header>
      {values.length === 0 ? (
        <NoData style={{ justifyContent: 'center' }} />
      ) : (
        <div>
          {values.map((value) => {
            return (
              <KnowledgeValue
                key={value.id}
                value={value}
                onSave={(nextValue) => {
                  return onUpdate({
                    ...knowledge,
                    values: values.map((value) => (value.id === nextValue.id ? nextValue : value)),
                  });
                }}
              />
            );
          })}
        </div>
      )}
    </Container>
  );
}

interface KnowledgeValueProps extends HTMLAttributes<HTMLDivElement> {
  value: Knowledge.SPU.KnowledgeValue;
  onSave: (value: Knowledge.SPU.KnowledgeValue) => Promise<any>;
}

function KnowledgeValue(props: KnowledgeValueProps) {
  const { value, onSave, ...otherProps } = props;
  const { t } = useTranslate();

  const {
    spus: [spu1, spu2, ...otherSpu],
  } = value;

  return (
    <ValueContainer {...otherProps}>
      {spu1 ? (
        <div>
          <Spu spu={spu1} />
          {spu2 ? <Spu spu={spu2} /> : null}
          {otherSpu.length > 0 && (
            <div style={{ textAlign: 'right', padding: '4px 12px' }}>
              <Popover
                trigger="click"
                content={
                  <SpuList>
                    {otherSpu.map((spu) => {
                      return (
                        <li key={spu.spuId}>
                          <Spu spu={spu} />
                        </li>
                      );
                    })}
                  </SpuList>
                }
              >
                <More>
                  {`${t('knowledge.更多x个商品', { x: otherSpu.length })}`}
                  <span>
                    <EllipsisOutlined />
                  </span>
                </More>
              </Popover>
            </div>
          )}
        </div>
      ) : (
        <div>
          <NoData />
        </div>
      )}
      <KnowledgeContent value={value} onSave={onSave} />
    </ValueContainer>
  );
}

export interface KnowledgeContentProps extends KnowledgeValueProps {}

export function KnowledgeContent(props: KnowledgeContentProps) {
  const { value, onSave, ...otherProps } = props;

  const { t } = useTranslate();

  const { getSegmentsValue } = useGetSegmentsValue();

  const [isEdit, setIsEdit] = useState(false);

  if (!isEdit) {
    return (
      <Tooltip title={t('knowledge.点击编辑')}>
        <div style={{ cursor: 'pointer' }} onClick={() => setIsEdit(true)} {...otherProps}>
          {contentRender(getSegmentsValue(value))}
        </div>
      </Tooltip>
    );
  }

  return (
    <Formik
      initialValues={{ value }}
      onSubmit={({ value }) => {
        return onSave(value)
          .then(() => {
            setIsEdit(false);
            message.success(t('operation.saveSucceed'));
          })
          .catch(() => {
            message.error(t('operation.saveFailed'));
          });
      }}
    >
      {({ dirty, isSubmitting, handleSubmit }) => {
        return (
          <FormContainer {...otherProps}>
            <FormField<Knowledge.SPU.KnowledgeValue>
              name="value"
              validate={(value) => validateUploadValue(segments2descendants(value.segments), t)}
            >
              {({ field: { value, onChange } }) => {
                return (
                  <KnowledgeContentItem
                    value={getSegmentsValue(value)}
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    onChange={useMemoizedFn((segments) =>
                      onChange({
                        ...value,
                        segments,
                      }),
                    )}
                  />
                );
              }}
            </FormField>
            <footer>
              <Button
                type="primary"
                disabled={!dirty}
                loading={isSubmitting}
                onClick={() => handleSubmit()}
              >
                {t('phrase.保存')}
              </Button>
              <Button type="link" onClick={() => setIsEdit(false)}>
                {t('marketing.取消')}
              </Button>
            </footer>
          </FormContainer>
        );
      }}
    </Formik>
  );
}

const NoData = (props: HTMLAttributes<HTMLDivElement>) => {
  const { t } = useTranslate();
  return <NoDataContainer {...props}>{t('storeSelector.暂无数据')}</NoDataContainer>;
};

export const contentRender = (segments: Knowledge.SPU.Segment[]) => {
  const descendants = segments2descendants(segments);

  if (isEmptyDescendants(descendants)) {
    return <NoData />;
  }

  return (
    <ReadOnlyEditorWrapper>
      <UploadEditor readOnly value={descendants} />
    </ReadOnlyEditorWrapper>
  );
};

export interface KnowledgeListProps
  extends Omit<ListProps<Knowledge.SPU.Knowledge>, 'keyRender' | 'itemRender'> {
  onOpenDrawer: ReturnType<typeof useDrawer>[0];
}

export function KnowledgeList(props: KnowledgeListProps) {
  const { refreshFn, onOpenDrawer, ...otherProps } = props;

  const { t } = useTranslate();

  const header = (
    <Header>
      <div>{t('knowledge.知识名称')}</div>
      <div>{t('knowledge.应用商品')}</div>
      <div>{t('feedback.内容')}</div>
    </Header>
  );

  return (
    <List
      {...otherProps}
      refreshFn={refreshFn}
      keyRender={(_) => _.id}
      header={header}
      itemRender={(knowledge) => {
        return (
          <Knowledge
            knowledge={knowledge}
            onEdit={() => {
              onOpenDrawer({
                sourceData: knowledge,
                refreshFn,
              });
            }}
            onUpdate={(spuKnowledge) => {
              return updateKnowledge({ spuKnowledge }).then(() => refreshFn());
            }}
          />
        );
      }}
    />
  );
}

const Header = styled.header`
  display: flex;
  align-items: center;
  position: sticky;
  top: -16px;
  z-index: 1;
  height: 56px;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: bold;
  background: linear-gradient(116deg, #edf5ff 0%, #d6e8ff 95%);
  > div:nth-child(1) {
    width: 200px;
    text-indent: 16px;
  }
  > div:nth-child(2) {
    width: 320px;
  }
`;

const Container = styled.div`
  display: flex;
  border: solid 1px #d3d7e0;
  border-radius: 2px;
  > header {
    flex: 0 0 auto;
    border-right: solid 1px #f0f2f5;
    > div {
      display: flex;
      position: sticky;
      top: 40px;
      width: 200px;
      padding: 16px;
      background: top left / contain no-repeat url(${`"${titleBackgroundImage}"`}),
        linear-gradient(180deg, #e0e9ff, #ffffff);
      > h4 {
        flex: 1 0 0;
        margin-bottom: 0;
      }
      > button {
        flex: 0 0 auto;
        margin-top: -4px;
        color: #515359;
      }
    }
  }
  > div {
    flex: 1 0 0;
    overflow: hidden;
  }
`;

const ValueContainer = styled.div`
  display: flex;
  border-bottom: solid 1px #f0f2f5;
  > div:first-of-type {
    flex: 0 0 auto;
    width: 318px;
    border-right: solid 1px #f0f2f5;
    > div {
      padding: 10px 12px;
    }
  }
  > div + div {
    flex: 1 0 0;
    display: flex;
    align-items: center;
    margin: 12px;
    padding: 8px;
    border-radius: 2px;
    background: #f4f6f8;
    overflow: hidden;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch !important;
  > footer {
    display: flex;
    align-items: center;
    > * + * {
      margin-left: 4px;
    }
  }
`;

const NoDataContainer = styled.div`
  flex: 1 0 0;
  display: flex;
  align-items: center;
  margin: 12px 0;
  color: #81868f;
`;

const ReadOnlyEditorWrapper = styled.div`
  flex: 1 0 0;
  align-self: stretch;
  overflow: hidden;
  > div {
    height: auto !important;
    background: transparent !important;
    border: none !important;
  }
  > div > div {
    padding: 0 !important;
    overflow: visible;
  }
  &:hover {
    overflow: auto;
  }
`;

export default Knowledge;
